<script setup lang="ts">
import lodashPick from 'lodash-es/pick';

export interface StoryblokSearchHeroProps {
    blok: StoryblokSearchHero;
}

// PROPS
const { blok } = defineProps<StoryblokSearchHeroProps>();

// COMPOSABLES
const { tt } = useTypedI18n();
const { classes } = useStoryblokComponentClass(() => lodashPick(blok, ['anchorId']));

const { inputValue, sellingMethod, isLoading, preview, onSubmit } = useQuickSearch();
</script>

<template>
    <!-- One number behind both the band's bottom padding and the panel's pull-up. -->
    <section :id="blok.anchorId" v-editable="blok" class="[--hero-overlap:7.125rem]" :class="classes">
        <BaseWidthContainer class="my-6 sm:my-8 xl:my-14">
            <div class="bg-primary-950 relative isolate overflow-hidden max-sm:-mx-6 sm:rounded-2xl">
                <div class="absolute inset-0">
                    <StoryblokComponent v-for="c in blok.cover ?? []" :key="c._uid" :blok="c" class="h-full w-full object-cover" />
                </div>
                <div class="from-primary-950/55 to-primary-950/80 absolute inset-0 bg-linear-to-b"></div>

                <div class="relative px-6 pt-10 pb-[calc(var(--hero-overlap)+2rem)] text-center sm:px-10 sm:pt-16">
                    <h1 class="text-inverted text-4xl font-semibold text-balance sm:text-5xl">{{ blok.title }}</h1>
                    <p v-if="!!blok.subtitle" class="text-inverted mt-2 text-2xl font-normal text-balance sm:text-3xl">{{ blok.subtitle }}</p>
                </div>
            </div>

            <!-- Sibling of the band, never a child: its `overflow-hidden` would clip the floating preview. -->
            <div class="relative z-20 mt-[calc(var(--hero-overlap)*-1)]">
                <div role="search" :aria-label="tt('search.quick.trigger')" class="mx-auto w-full max-w-3xl">
                    <QuickSearchPanel
                        v-model="inputValue"
                        v-model:selling-method="sellingMethod"
                        :is-loading="isLoading"
                        :preview="preview"
                        @submit="onSubmit()"
                    />
                </div>

                <QuickSearchTrustPoints :points="blok.trustPoints" class="mx-auto mt-8 max-w-3xl" />
            </div>
        </BaseWidthContainer>
    </section>
</template>
